import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

// ---------------------------------------------------------------------
// DESIGN ONLY — NOT A SECURITY CHECK.
// The code is not sent to or verified by the backend: any 6 digits pass.
// To make it real: have /login email the code and return a temporary
// token, then replace `verify` / `resend` below with API calls
// (e.g. postRequest('/login/verify-otp', { tempToken, otp })).
// ---------------------------------------------------------------------

const LENGTH = 6;
const RESEND_AFTER = 30; // seconds

interface OtpStepProps {
    email: string;
    onVerified: () => void;
    onBack: () => void;
}

const OtpStep = ({ email, onVerified, onBack }: OtpStepProps) => {
    const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(""));
    const [error, setError] = useState("");
    const [shake, setShake] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [seconds, setSeconds] = useState(RESEND_AFTER);
    const inputs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => { inputs.current[0]?.focus(); }, []);

    // resend countdown
    useEffect(() => {
        if (seconds <= 0) return;
        const t = setTimeout(() => setSeconds(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [seconds]);

    const setAt = (i: number, v: string) => setDigits(d => d.map((x, k) => (k === i ? v : x)));

    const onChange = (i: number, value: string) => {
        const v = value.replace(/\D/g, "");
        setError("");
        if (v.length > 1) {
            // pasted or autofilled several digits
            const next = [...digits];
            v.slice(0, LENGTH - i).split("").forEach((ch, k) => { next[i + k] = ch; });
            setDigits(next);
            inputs.current[Math.min(i + v.length, LENGTH - 1)]?.focus();
            return;
        }
        setAt(i, v);
        if (v && i < LENGTH - 1) inputs.current[i + 1]?.focus();
    };

    const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) {
            setAt(i - 1, "");
            inputs.current[i - 1]?.focus();
        } else if (e.key === "ArrowLeft" && i > 0) {
            inputs.current[i - 1]?.focus();
        } else if (e.key === "ArrowRight" && i < LENGTH - 1) {
            inputs.current[i + 1]?.focus();
        } else if (e.key === "Enter") {
            verify();
        }
    };

    const verify = () => {
        if (verifying) return;
        if (digits.some(d => !d)) {
            setError("Enter the 6-digit code from your email");
            setShake(true);
            setTimeout(() => setShake(false), 400);
            return;
        }
        setVerifying(true);
        // design only: accept any complete code
        setTimeout(() => {
            setVerifying(false);
            onVerified();
        }, 500);
    };

    // auto-verify as soon as all 6 digits are in (typed, pasted or autofilled);
    // remembers the last code so the same code isn't submitted twice
    const lastAutoCode = useRef("");
    useEffect(() => {
        const code = digits.join("");
        if (code.length !== LENGTH) { lastAutoCode.current = ""; return; }
        if (code === lastAutoCode.current) return;
        lastAutoCode.current = code;
        inputs.current[LENGTH - 1]?.blur();
        verify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [digits]);

    const resend = () => {
        setDigits(Array(LENGTH).fill(""));
        setError("");
        setSeconds(RESEND_AFTER);
        inputs.current[0]?.focus();
        toast.success(`A new code was sent to ${email}`);
    };

    const initial = (email.trim()[0] || "A").toUpperCase();

    return (
        <>
            <button type="button" className="au-back" onClick={onBack}><i className="ri-arrow-left-line"></i>Back</button>
            <span className="au-eyebrow"><i className="ri-mail-check-line"></i>Step 2 of 2</span>
            <h2>Verify it's you</h2>
            <p className="sub">We've emailed a 6-digit code to your staff email. Enter it below to continue.</p>

            <div className="au-who">
                <div className="av">{initial}</div>
                <div style={{ minWidth: 0 }}>
                    <b>{email}</b>
                    <span>Code valid for 10 minutes</span>
                </div>
            </div>

            {error && <div className="au-alert"><i className="ri-error-warning-line"></i><div>{error}</div></div>}

            <div className={`au-otp ${shake ? "err" : ""}`}>
                {digits.map((d, i) => (
                    <input
                        key={i}
                        ref={el => { inputs.current[i] = el; }}
                        className={d ? "fill" : ""}
                        value={d}
                        inputMode="numeric"
                        autoComplete={i === 0 ? "one-time-code" : "off"}
                        aria-label={`Digit ${i + 1}`}
                        maxLength={LENGTH}
                        onChange={(e) => onChange(i, e.target.value)}
                        onKeyDown={(e) => onKeyDown(i, e)}
                        onFocus={(e) => e.target.select()}
                    />
                ))}
            </div>

            <div className="au-otp-row">
                <span className="muted" style={{ color: "var(--muted)" }}>
                    {seconds > 0 ? `Resend code in ${seconds}s` : "Didn't get the code?"}
                </span>
                <button type="button" className="au-link" disabled={seconds > 0} onClick={resend}>Resend code</button>
            </div>

            <button type="button" className="au-btn" onClick={verify} disabled={verifying}>
                {verifying && <span className="au-spin"></span>}
                <span>{verifying ? "Verifying…" : "Verify & sign in"}</span>
            </button>

            <div className="au-note">
                <i className="ri-question-line"></i>
                <div>Can't find the email? Check your spam folder, or ask a Super Admin to confirm your staff email address.</div>
            </div>
        </>
    );
};

export default OtpStep;
